/**
 * Сборка экранов в одно приложение (тикет T18b).
 *
 * Порядок такой: сперва выясняем, вошёл ли человек, и только потом спрашиваем его
 * Настройки. Спроси мы Настройки раньше, невошедший увидел бы на странице входа отказ
 * сервера — и решил бы, что сломалось приложение, а не что он просто ещё не вошёл.
 *
 * До ответа сервера экран не мигает содержимым: показывается ожидание. Мелькнувшая на
 * долю секунды форма входа у того, кто уже вошёл, читается как «меня выкинуло».
 */
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useRoute } from './routing/use-route.js';
import { SettingsProvider } from './state/settings.js';
import { SignIn } from './screens/SignIn.js';
import { JoinInvite } from './screens/JoinInvite.js';
import { Workspace } from './screens/Workspace.js';
import { Settings } from './screens/Settings.js';
import { createApi } from './net/api.js';

type Session = 'unknown' | 'in' | 'out';

export function App(): ReactNode {
  const [route, go] = useRoute();
  const spaceSlug = route.spaceSlug;
  const [session, setSession] = useState<Session>('unknown');
  const joining = route.name === 'join';

  useEffect(() => {
    // По Приглашению приходит тот, у кого Учётной записи ещё нет: спрашивать сервер,
    // вошёл ли он, значит задержать экран ради заведомого «нет».
    if (!spaceSlug || joining) {
      setSession('out');
      return;
    }
    let alive = true;
    setSession('unknown');
    createApi(spaceSlug)
      .me()
      .then(() => {
        if (alive) setSession('in');
      })
      .catch(() => {
        // Сюда попадает и «не вошёл», и «сервер молчит». Разницы для экрана нет: без
        // ответа сервера рабочее место всё равно не собрать, а форма входа хотя бы даёт
        // человеку что-то сделать.
        if (alive) setSession('out');
      });
    return () => {
      alive = false;
    };
  }, [spaceSlug, joining]);

  const signedOut = useCallback(() => {
    setSession('out');
    go({ name: 'signIn', spaceSlug });
  }, [go, spaceSlug]);

  const signedIn = useCallback(
    (slug: string) => {
      setSession('in');
      go({ name: 'channel', spaceSlug: slug, channelId: null }, true);
    },
    [go],
  );

  const inside = session === 'in' && spaceSlug !== null;

  return (
    <SettingsProvider spaceSlug={inside ? spaceSlug : null}>
      {route.name === 'join' ? (
        <JoinInvite
          spaceSlug={route.spaceSlug}
          token={route.token}
          onJoined={signedIn}
          onSignIn={() => go({ name: 'signIn', spaceSlug })}
        />
      ) : session === 'unknown' ? (
        <main id="main" className="grid min-h-dvh place-items-center p-6 text-ink-soft">
          {/* Пока ждём — говорим об этом и голосом: пустой экран не объясняет ничего. */}
          <p role="status" aria-live="polite">
            …
          </p>
        </main>
      ) : !inside ? (
        <SignIn spaceSlug={spaceSlug} onSignedIn={signedIn} />
      ) : route.name === 'settings' ? (
        <Settings onBack={() => go({ name: 'channel', spaceSlug, channelId: null })} />
      ) : (
        <Workspace
          spaceSlug={spaceSlug}
          channelId={route.name === 'channel' ? route.channelId : null}
          onGo={go}
          onSignedOut={signedOut}
        />
      )}
    </SettingsProvider>
  );
}
